import { z } from 'zod';

/**
 * Wire format shared by every id: opaque, URL-safe, 1 to 64 characters.
 *
 * Why opaque: engine ids (PostgreSQL uuids, MongoDB position ids) differ in shape, and how they
 * map to FXMate ids depends on Q34 to Q36. The client never parses an id. Proposal, to be
 * validated (C1 spec R5).
 */
export const OPAQUE_ID_PATTERN = /^[A-Za-z0-9_-]{1,64}$/;

function opaqueId(description: string) {
  return z.string().regex(OPAQUE_ID_PATTERN, 'Expected an opaque id').describe(description);
}

/** Trader id. The API reads it from the access token only, never from a body or path (master X7). */
export const TraderIdSchema = opaqueId('Opaque trader id').brand<'TraderId'>();
/** Branded trader id (standards §1). */
export type TraderId = z.infer<typeof TraderIdSchema>;

/** Telegram channel id as exposed by the API (maps to an engine master account, Q36). */
export const ChannelIdSchema = opaqueId('Opaque channel id').brand<'ChannelId'>();
/** Branded channel id. */
export type ChannelId = z.infer<typeof ChannelIdSchema>;

/** Subscription of the trader to one channel (FXM-02 §5 channel states). */
export const SubscriptionIdSchema = opaqueId('Opaque subscription id').brand<'SubscriptionId'>();
/** Branded subscription id. */
export type SubscriptionId = z.infer<typeof SubscriptionIdSchema>;

/** Position copied by FXMate or opened manually (FXM-04 §4). */
export const PositionIdSchema = opaqueId('Opaque position id').brand<'PositionId'>();
/** Branded position id. */
export type PositionId = z.infer<typeof PositionIdSchema>;

/** One immutable version of a channel's settings (master X10). */
export const SettingsVersionIdSchema = opaqueId(
  'Opaque settings version id',
).brand<'SettingsVersionId'>();
/** Branded settings version id. */
export type SettingsVersionId = z.infer<typeof SettingsVersionIdSchema>;

/** One immutable version of the account limits (technical spec §6, PUT /account/limits). */
export const AccountLimitsVersionIdSchema = opaqueId(
  'Opaque account limits version id',
).brand<'AccountLimitsVersionId'>();
/** Branded account limits version id. */
export type AccountLimitsVersionId = z.infer<typeof AccountLimitsVersionIdSchema>;

/** One emergency stop (`stopId`), recorded append-only by the API (M4 R17; C1 R5, Decision 6). */
export const EmergencyStopIdSchema = opaqueId(
  'Opaque emergency stop id',
).brand<'EmergencyStopId'>();
/** Branded emergency stop id. */
export type EmergencyStopId = z.infer<typeof EmergencyStopIdSchema>;
